/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { setTitle } from "@lemmonhq/orders";
import { ErrorRenderer, Loading, ResponsiveTable } from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import { useToast } from "@app/vue/providers/Toast";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import type { PrintTemplate } from "@lemmonhq/types";
import type { PrintTemplateEditorMode, PrintTemplateFormState } from "./types";
import { PrintTemplateEditorSheet } from "./PrintTemplateEditorSheet";
import {
  getPrintTemplateTypeDescription,
  getPrintTemplateTypeLabel,
  PRINT_TEMPLATE_QUERY_KEY,
} from "./utils";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";

export const PrintTemplatesSettings = defineComponent({
  name: "PrintTemplatesSettings",
  setup() {
    const trpc = useTrpc();
    const toast = useToast();
    const queryClient = useQueryClient();

    const isSheetOpen = ref(false);
    const mode = ref<PrintTemplateEditorMode>("create");
    const selectedTemplate = ref<PrintTemplate | null>(null);

    const listQuery = useQuery({
      queryKey: [...PRINT_TEMPLATE_QUERY_KEY, "list"],
      queryFn: () => trpc.printTemplates.list.query({}),
    });

    const createMutation = useMutation({
      mutationFn: (input: PrintTemplateFormState) =>
        trpc.printTemplates.create.mutate(input),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: PRINT_TEMPLATE_QUERY_KEY,
        });
        toast.showToast("Print template created.", { type: "success" });
      },
      onError: () => {
        toast.showToast("Failed to create print template.", {
          type: "error",
        });
      },
    });

    const updateMutation = useMutation({
      mutationFn: (input: { id: number; data: PrintTemplateFormState }) =>
        trpc.printTemplates.update.mutate(input),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: PRINT_TEMPLATE_QUERY_KEY,
        });
        toast.showToast("Print template updated.", { type: "success" });
      },
      onError: () => {
        toast.showToast("Failed to update print template.", {
          type: "error",
        });
      },
    });

    const deleteMutation = useMutation({
      mutationFn: (id: number) => trpc.printTemplates.delete.mutate({ id }),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: PRINT_TEMPLATE_QUERY_KEY,
        });
        toast.showToast("Print template deleted.", { type: "success" });
      },
      onError: () => {
        toast.showToast("Failed to delete print template.", {
          type: "error",
        });
      },
    });

    const openCreateSheet = () => {
      mode.value = "create";
      selectedTemplate.value = null;
      isSheetOpen.value = true;
    };

    const openEditSheet = (template: PrintTemplate) => {
      mode.value = "edit";
      selectedTemplate.value = template;
      isSheetOpen.value = true;
    };

    const closeSheet = () => {
      isSheetOpen.value = false;
      selectedTemplate.value = null;
      mode.value = "create";
    };

    const handleSubmit = async (data: PrintTemplateFormState) => {
      if (mode.value === "create") {
        await createMutation.mutateAsync(data);
      } else if (selectedTemplate.value) {
        await updateMutation.mutateAsync({
          id: selectedTemplate.value.id,
          data,
        });
      }

      closeSheet();
    };

    const handleDelete = async () => {
      if (!selectedTemplate.value) return;
      await deleteMutation.mutateAsync(selectedTemplate.value.id);
      closeSheet();
    };

    onMounted(() => {
      setTitle("Print Templates");
    });

    const columns: ResponsiveTableColumn[] = [
      {
        key: "d-name",
        label: "Name",
        icon: "print",
        mobilePriority: "hidden",
        render: (template: PrintTemplate) => (
          <div class="font-medium text-base-content">{template.name}</div>
        ),
      },
      {
        key: "d-type",
        label: "Type",
        icon: "category",
        mobilePriority: "hidden",
        render: (template: PrintTemplate) => (
          <span class="badge badge-outline badge-sm">
            {getPrintTemplateTypeLabel(template.type)}
          </span>
        ),
      },
      {
        key: "d-description",
        label: "Usage",
        icon: "description",
        mobilePriority: "hidden",
        render: (template: PrintTemplate) => (
          <span class="text-sm text-base-content/70">
            {getPrintTemplateTypeDescription(template.type)}
          </span>
        ),
      },
      {
        key: "m-name",
        label: "Name",
        desktopHide: true,
        mobilePriority: "primary",
        render: (template: PrintTemplate) => (
          <div class="min-w-0">
            <div class="truncate text-sm font-medium text-base-content">
              {template.name}
            </div>
            <div class="mt-0.5 truncate text-xs text-base-content/60">
              {getPrintTemplateTypeDescription(template.type)}
            </div>
          </div>
        ),
      },
      {
        key: "m-type",
        label: "Type",
        desktopHide: true,
        mobilePriority: "primary",
        mobilePrimaryClass: "flex items-center shrink-0",
        render: (template: PrintTemplate) => (
          <span class="badge badge-outline badge-sm">
            {getPrintTemplateTypeLabel(template.type)}
          </span>
        ),
      },
      {
        key: "actions",
        label: "",
        mobilePriority: "hidden",
        headerClass: "w-px",
        cellClass: "text-right whitespace-nowrap",
        render: (template: PrintTemplate) => (
          <button
            class="btn btn-sm btn-secondary"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              openEditSheet(template);
            }}
          >
            <span class="material-symbols-outlined text-2xl">table_edit</span>
          </button>
        ),
      },
    ];

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="print"
          title="Print templates"
          description="Create and maintain receipt and kitchen ticket layouts."
        >
          {{
            actions: () => (
              <button
                disabled={listQuery.isPending.value}
                class="btn btn-primary btn-sm gap-2 shadow-lg"
                onClick={openCreateSheet}
              >
                <span class="material-symbols-outlined text-base">add</span>
                New template
              </button>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent noPadding>
            {listQuery.isPending.value ? (
              <div class="flex h-80 items-center justify-center">
                <Loading />
              </div>
            ) : listQuery.isError.value ? (
              <ErrorRenderer
                error={
                  listQuery.error.value instanceof Error
                    ? listQuery.error.value
                    : null
                }
                message="Failed to load print templates."
                onRetry={() => void listQuery.refetch()}
              />
            ) : !listQuery.data.value || listQuery.data.value.length === 0 ? (
              <div class="flex min-h-[320px] flex-col items-center justify-center px-8 py-16 text-center">
                <div class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-base-200">
                  <span class="material-symbols-outlined text-3xl text-base-content/40">
                    print
                  </span>
                </div>
                <p class="mb-2 text-base font-medium text-base-content">
                  No print templates yet
                </p>
                <p class="max-w-md text-sm text-base-content/60">
                  Add your first template to manage kitchen tickets and tax
                  receipts from one place.
                </p>
                <button
                  class="btn btn-primary btn-sm mt-5"
                  onClick={openCreateSheet}
                >
                  Create template
                </button>
              </div>
            ) : (
              <ResponsiveTable
                columns={columns}
                rows={listQuery.data.value}
                rowKey={(template: PrintTemplate) => template.id}
                onRowClick={openEditSheet}
                emptyMessage="No print templates yet"
              />
            )}
          </AppSubpageContent>
        </AppSubpage>
        {isSheetOpen.value ? (
          <PrintTemplateEditorSheet
            isOpen={isSheetOpen.value}
            mode={mode.value}
            template={selectedTemplate.value}
            onClose={closeSheet}
            onSubmit={handleSubmit}
            onDelete={mode.value === "edit" ? handleDelete : undefined}
          />
        ) : null}
      </div>
    );
  },
});
